import React, { Fragment, useEffect, useRef, type FC } from 'react';
import { css } from '@compiled/react';
import { useFragment } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useContentType } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { HighlightActionsProviderForNav4 } from '@confluence/highlight-actions/entry-points/HighlightActionsProvider.ts';
import {
	useObjectSidebarActions,
	useIsObjectSidebarShown,
	useBehavior,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { ObjectSidebarPushPlaceholder } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPushPlaceholder.tsx';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	APPROVALS_PANE_CURRENT_CONTENT_STATUS,
	APPROVALS_PANE_ID,
	getApprovalsPaneContextKey,
	syncApprovalsPaneContextOwnership,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { ContentScreenComponent } from './ContentScreenComponent.tsx';
import { ContentContainerComponent } from './ContentContainerComponent.js';
import { ContentContainerThemeFragment } from './ContentContainerTheme.graphql.ts';
import type {
	ContentContainerTheme_space$data,
	ContentContainerTheme_space$key,
} from './__generated__/ContentContainerTheme_space.graphql.ts';

const EMPTY_CONTENT_SCREEN_STYLES = {};
const EMPTY_LOOK_AND_FEEL = {};

const useContentRecommendationParam = createSingleQueryParamHook('contentRecommendation');

const contentWrapperStyles = css({
	display: 'block',
	flex: '2',
	width: '100%',
});

type ContentContainerProps = {
	contentId: string;
	fetchTheme?: boolean;
	children:
		| React.ReactNode
		| ((props: { isThemed: boolean; isLoading: boolean }) => React.ReactNode);
	isScreenContentView?: boolean;
	isContainerContentView?: boolean;
	spaceKey?: string;
	errorAttribution: string;
	contentScreenStyles?: any;
	stickyHeader?: React.ReactNode;
	versionOverride?: number | null;
	isEmbeddedPage?: boolean;
	contentThemeSpaceData?: ContentUnifiedQueryType['space'] | ContentBlogUnifiedQueryType['space'];
};

type ContentContainerThemeData =
	| ContentUnifiedQueryType['space']
	| ContentBlogUnifiedQueryType['space']
	| ContentContainerTheme_space$data;

type ContentContainerThemeRendererProps = Pick<
	ContentContainerProps,
	| 'children'
	| 'spaceKey'
	| 'errorAttribution'
	| 'contentScreenStyles'
	| 'stickyHeader'
	| 'isEmbeddedPage'
> & {
	isScreenContentView: boolean;
	isContainerContentView: boolean;
	isLivePage: boolean;
	isObjectSidebarShown: boolean;
	pageMode: PageMode;
	behavior: ReturnType<typeof useBehavior>;
	shouldAddBannerPadding: boolean;
	themeData?: ContentContainerThemeData | null;
	loading: boolean;
	error?: Error;
};

const ContentContainerThemeRenderer: FC<ContentContainerThemeRendererProps> = ({
	children,
	spaceKey,
	errorAttribution,
	contentScreenStyles = EMPTY_CONTENT_SCREEN_STYLES,
	stickyHeader = null,
	isEmbeddedPage,
	isScreenContentView,
	isContainerContentView,
	isLivePage,
	isObjectSidebarShown,
	pageMode,
	behavior,
	shouldAddBannerPadding,
	themeData,
	loading,
	error,
}) => {
	const theme = Boolean(themeData?.theme);
	const lookAndFeel = themeData?.lookAndFeel || EMPTY_LOOK_AND_FEEL;

	// The error branch is reachable when a viewer can read the content but not the space, which is
	// what an access link creates. `children` may be a render prop there, and handing the function
	// to React renders nothing and only warns — so resolve it before the branch, not after.
	const childrenToRender =
		typeof children === 'function' ? children({ isThemed: theme, isLoading: loading }) : children;

	if (error) {
		return (
			<ErrorBoundary attribution={errorAttribution}>
				{childrenToRender}
				<ErrorDisplay error={error} />
			</ErrorBoundary>
		);
	}

	return (
		<ErrorBoundary attribution={errorAttribution}>
			<ContentScreenComponent
				spaceKey={spaceKey}
				isThemed={theme}
				isLoading={loading}
				lookAndFeel={lookAndFeel}
				isContentView={isScreenContentView}
				contentScreenStyles={contentScreenStyles}
				isLivePage={isLivePage}
				isEmbeddedPage={isEmbeddedPage}
			>
				{!theme && stickyHeader ? stickyHeader : null}
				<ContentContainerComponent
					isThemed={theme}
					lookAndFeel={lookAndFeel}
					isContentView={isContainerContentView}
					isLoading={loading}
					isLivePage={isLivePage}
					additionalPaddingTop={shouldAddBannerPadding ? 12 : 0}
				>
					<Fragment>
						<div css={contentWrapperStyles}>{childrenToRender}</div>
						{/* When panel-system flag is enabled, Object Sidebar renders at Root level via navigation-system Panel */}
						{!fg('ads-panel-system-confluence-object-sidebar') &&
							isObjectSidebarShown &&
							pageMode === PageMode.VIEW && (
								<Fragment>
									{behavior === 'push' && <ObjectSidebarPushPlaceholder />}
									<ObjectSidebarPanel ignoreGap />
								</Fragment>
							)}
					</Fragment>
				</ContentContainerComponent>
				<HighlightActionsProviderForNav4 />
			</ContentScreenComponent>
		</ErrorBoundary>
	);
};

const ContentContainerWithThemeFragment: FC<
	Omit<ContentContainerThemeRendererProps, 'themeData' | 'loading' | 'error'> & {
		contentThemeSpaceData: ContentContainerTheme_space$key | null;
	}
> = ({ contentThemeSpaceData, ...props }) => {
	const themeData = useFragment(ContentContainerThemeFragment, contentThemeSpaceData);

	return <ContentContainerThemeRenderer {...props} themeData={themeData} loading={false} />;
};

const ContentContainerWithThemeQuery: FC<
	Omit<ContentContainerThemeRendererProps, 'themeData' | 'loading' | 'error'> & {
		contentId: string;
		spaceKey?: string;
		versionOverride?: number | null;
		isBlogpost: boolean;
		fetchTheme: boolean;
	}
> = ({ contentId, spaceKey, versionOverride, isBlogpost, fetchTheme, ...props }) => {
	const skipThemeQuery = !fetchTheme || !contentId;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const pageThemeQueryResult = useQueryInterop<
		ContentUnifiedQueryType,
		ContentUnifiedQueryVariablesType
	>(ContentUnifiedQueryArtifact, {
		context: { single: true },
		skip: skipThemeQuery || isBlogpost,
		variables: {
			contentId,
			spaceKey,
			versionOverride,
			isSSR,
			useNewContentTopper: true,
		},
	});

	const blogThemeQueryResult = useQueryInterop<
		ContentBlogUnifiedQueryType,
		ContentBlogUnifiedQueryVariablesType
	>(ContentBlogUnifiedQueryArtifact, {
		context: { single: true },
		skip: skipThemeQuery || !isBlogpost,
		variables: {
			contentId,
			spaceKey,
			isSSR,
			versionOverride,
			useNewContentTopper: true,
		},
	});

	const queryResult = isBlogpost ? blogThemeQueryResult : pageThemeQueryResult;

	return (
		<ContentContainerThemeRenderer
			{...props}
			themeData={queryResult.data?.space}
			loading={queryResult.loading}
			error={queryResult.error}
			spaceKey={spaceKey}
		/>
	);
};

export const ContentContainer: FC<ContentContainerProps> = ({
	contentId,
	fetchTheme = false,
	children,
	isScreenContentView = false,
	isContainerContentView = false,
	spaceKey,
	errorAttribution = Attribution.BACKBONE as string,
	contentScreenStyles = EMPTY_CONTENT_SCREEN_STYLES,
	stickyHeader = null,
	versionOverride = null,
	isEmbeddedPage,
	contentThemeSpaceData,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const [contentType] = useContentType();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const behavior = useBehavior();
	const pageMode = useGetPageMode();

	const contentRecommendationParam = useContentRecommendationParam();

	const rightSidebarContext = useRightSidebarContext();
	const isSidebarApprovalsPaneOpen =
		rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID && pageMode === PageMode.VIEW;
	const approvalsPaneContextKey = getApprovalsPaneContextKey({
		contentId,
		contentStatus: APPROVALS_PANE_CURRENT_CONTENT_STATUS,
	});

	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	useEffect(() => {
		void hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	useEffect(() => {
		syncApprovalsPaneContextOwnership({
			isApprovalsExperimentEnabled: expValEquals('confluence_approvals_eap', 'isEnabled', true),
			isSidebarApprovalsPaneOpen,
			contextKey: approvalsPaneContextKey,
			closeApprovalsPane: () => rightSidebarContextRef.current.close(APPROVALS_PANE_ID),
		});
	}, [approvalsPaneContextKey, isSidebarApprovalsPaneOpen]);

	const isBlogpost = contentType === 'blogpost';
	const hasParentThemeData = contentThemeSpaceData !== undefined;

	const shouldAddBannerPadding =
		!isLivePage &&
		contentRecommendationParam === 'on' &&
		fg('cc-engagement-nth-user-teammate-rollout-gate');

	const sharedProps = {
		children,
		spaceKey,
		errorAttribution,
		contentScreenStyles,
		stickyHeader,
		isEmbeddedPage,
		isScreenContentView,
		isContainerContentView,
		isLivePage,
		isObjectSidebarShown,
		pageMode,
		behavior,
		shouldAddBannerPadding,
	};

	if (hasParentThemeData && fg('confluence_frontend_view_page_relay_fragment_dedup')) {
		return (
			<ContentContainerWithThemeFragment
				{...sharedProps}
				contentThemeSpaceData={contentThemeSpaceData as ContentContainerTheme_space$key | null}
			/>
		);
	}

	return (
		<ContentContainerWithThemeQuery
			{...sharedProps}
			contentId={contentId}
			spaceKey={spaceKey}
			versionOverride={versionOverride}
			isBlogpost={isBlogpost}
			fetchTheme={fetchTheme}
		/>
	);
};
